import { useState, useEffect, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import ImageUploader from './components/ImageUploader';
import ListingResult from './components/ListingResult';
import PaywallModal from './components/PaywallModal';
import LoginSheet from './components/LoginSheet';
import SettingsView from './components/SettingsView';
import AccountView from './components/AccountView';
import QuickAddDetails from './components/QuickAddDetails';
import PrivacyPolicy from './components/PrivacyPolicy';
import LandingPage from './components/LandingPage';
import VerifyEmailPage from './components/VerifyEmailPage';

// ─── Constants ───────────────────────────────────────────────────────────────
const FREE_GENERATION_LIMIT = 3;
const LOCATION_STORAGE_KEY = 'sold4you_location';

const VIEW = { UPLOAD: 'upload', LOADING: 'loading', RESULT: 'result' };
const TAB  = { GENERATE: 'generate', SETTINGS: 'settings', ACCOUNT: 'account' };

const DEFAULT_SETTINGS = {
  tone: 'regular',
  listingStyle: 'playful', // 'playful' (emojis) | 'minimalist' (clean)
  spacing: 'spaced',
  condition: 'Good',
};

// Persistent pickup-location memory (localStorage)
function loadSavedLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.suburb && !parsed?.postcode) return null;
    return { suburb: parsed.suburb || '', postcode: parsed.postcode || '' };
  } catch {
    return null;
  }
}

function buildDefaultQuickAdd() {
  const saved = loadSavedLocation();
  return {
    dimensions: { enabled: false, leaveBlank: false, mode: 'dimensions', l: '', w: '', h: '', size: '' },
    price:      { enabled: false, leaveBlank: false, includeRrp: false, value: '', rrp: '' },
    location:   {
      enabled: false,
      leaveBlank: false,
      suburb: saved?.suburb || '',
      postcode: saved?.postcode || '',
      save: !!saved, // if a location was previously saved, keep it saved on re-generate
    },
  };
}

const LISTING_MODES = [
  { id: 1, label: 'Single',  icon: '📦', desc: 'One item, one listing' },
  { id: 2, label: 'Bundle',  icon: '🎁', desc: 'Multiple items, one listing' },
  { id: 3, label: 'Bulk',    icon: '📋', desc: 'Each photo = separate listing' },
];

const LOADING_STEPS = [
  'Analyzing your photos…',
  'Reading condition & details…',
  'Applying your settings…',
  'Writing your listing…',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const COMPRESS_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.85,
};

async function compressAndEncode(file) {
  const compressed = await imageCompression(file, COMPRESS_OPTIONS);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(compressed);
  });
}

function saveSession(token, user) {
  sessionStorage.setItem('fliplist_token', token);
  sessionStorage.setItem('fliplist_user', JSON.stringify(user));
}

function clearSession() {
  sessionStorage.removeItem('fliplist_token');
  sessionStorage.removeItem('fliplist_user');
}

// ─── App ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [activeTab,          setActiveTab]          = useState(TAB.GENERATE);
  const [view,               setView]               = useState(VIEW.UPLOAD);
  const [images,             setImages]             = useState([]);
  const [listing,            setListing]            = useState(null);
  const [listingId,          setListingId]          = useState(null);
  const [error,              setError]              = useState(null);
  const [loadingStep,        setLoadingStep]        = useState(0);
  const [showPaywall,        setShowPaywall]        = useState(false);
  const [listingMode,        setListingMode]        = useState(1);
  const [settings,           setSettings]           = useState(DEFAULT_SETTINGS);
  const [quickAdd,           setQuickAdd]           = useState(buildDefaultQuickAdd);
  const [hasSavedLocation,   setHasSavedLocation]   = useState(() => !!loadSavedLocation());
  const [activeListingIndex, setActiveListingIndex] = useState(0);
  const [isCompressing,      setIsCompressing]      = useState(false);
  const [showLoginSheet,     setShowLoginSheet]     = useState(false);
  const [loginSheetMode,     setLoginSheetMode]     = useState('login');

  // ── Auth state
  const [token, setToken] = useState(() => sessionStorage.getItem('fliplist_token'));
  const [user,  setUser]  = useState(() => {
    const s = sessionStorage.getItem('fliplist_user');
    return s ? JSON.parse(s) : null;
  });

  const isAuthenticated = !!token && !!user;
  const listingsCount   = user?.listings_count ?? 0;
  const isSubscribed    = user?.is_subscribed ?? false;
  const promoActive     = !!(user?.promo_access_until && new Date(user.promo_access_until) > new Date());
  const hasAccess       = isSubscribed || promoActive;
  const remainingFree   = Math.max(0, FREE_GENERATION_LIMIT - listingsCount);
  const isAtLimit       = !hasAccess && listingsCount >= FREE_GENERATION_LIMIT;

  // ── Refresh user from DB on every load (picks up subscription changes, webhook updates)
  useEffect(() => {
    if (!token) return;
    fetch('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (r.status === 401) { handleLogout(); return null; } return r.json(); })
      .then((data) => {
        if (!data?.success) return;
        const fresh = data.data.user;
        setUser(fresh);
        sessionStorage.setItem('fliplist_user', JSON.stringify(fresh));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Loading step animation
  useEffect(() => {
    if (view !== VIEW.LOADING) { setLoadingStep(0); return; }
    const t = setInterval(() => setLoadingStep((p) => Math.min(p + 1, LOADING_STEPS.length - 1)), 700);
    return () => clearInterval(t);
  }, [view]);

  // ── Auth handlers
  const handleLogout = useCallback(() => {
    clearSession(); setToken(null); setUser(null);
    setView(VIEW.UPLOAD); setImages([]); setListing(null); setError(null);
  }, []);

  const handle401 = useCallback(() => {
    clearSession(); setToken(null); setUser(null);
    setError('Your session expired — please sign in again.');
    setView(VIEW.UPLOAD);
  }, []);

  const handleLogin = useCallback(async (email, password) => {
    const res  = await fetch('/auth/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.error?.message || 'Login failed.');
      err.code  = data.error?.code;
      err.email = email;
      throw err;
    }
    saveSession(data.data.token, data.data.user);
    setToken(data.data.token); setUser(data.data.user);
  }, []);

  const handleRegister = useCallback(async (email, password) => {
    const res  = await fetch('/auth/register', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error?.message || 'Registration failed.');
    // Email verification required — don't log in yet; return signal to LoginSheet
    if (data.data?.requiresVerification) {
      return { requiresVerification: true, email: data.data.email };
    }
    saveSession(data.data.token, data.data.user);
    setToken(data.data.token); setUser(data.data.user);
  }, []);

  const handleGoogleLogin = useCallback(async (idToken) => {
    const res  = await fetch('/auth/google', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ idToken }) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error?.message || 'Google sign-in failed.');
    saveSession(data.data.token, data.data.user);
    setToken(data.data.token); setUser(data.data.user);
  }, []);

  // ── Generate
  const handleGenerate = useCallback(async () => {
    if (images.length === 0 || !isAuthenticated) return;
    if (!hasAccess && listingsCount >= FREE_GENERATION_LIMIT) { setShowPaywall(true); return; }

    setError(null);
    setIsCompressing(true);
    setView(VIEW.LOADING);

    try {
      // Compress all photos in parallel before sending to the backend.
      // useWebWorker:true keeps the UI thread responsive during compression.
      const base64Images = await Promise.all(images.map(compressAndEncode));
      setIsCompressing(false);

      const res = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ images: base64Images, listingMode, settings, quickAdd }),
      });

      if (res.status === 401) { handle401(); return; }

      const data = await res.json();
      if (!data.success) throw new Error(data.error?.message || 'Generation failed.');

      // ── Sync pickup-location memory with the user's choice.
      //    Only act when the location toggle was actually used (and not left blank).
      const loc = quickAdd.location;
      if (loc.enabled && !loc.leaveBlank) {
        try {
          if (loc.save && (loc.suburb || loc.postcode)) {
            localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({ suburb: loc.suburb, postcode: loc.postcode }));
            setHasSavedLocation(true);
          } else if (!loc.save) {
            // User opted out of saving — forget any previously stored location
            localStorage.removeItem(LOCATION_STORAGE_KEY);
            setHasSavedLocation(false);
          }
        } catch { /* localStorage unavailable — non-fatal */ }
      }

      const updated = { ...user, listings_count: listingsCount + 1 };
      setUser(updated);
      sessionStorage.setItem('fliplist_user', JSON.stringify(updated));

      setActiveListingIndex(0);
      setListingId(data.data.listingId ?? null);
      setListing(data.data);
      setView(VIEW.RESULT);
    } catch (err) {
      console.error('[App] Generation failed:', err);
      setIsCompressing(false);
      setError('Something went wrong. Please try again.');
      setView(VIEW.UPLOAD);
    }
  }, [images, token, user, isAuthenticated, hasAccess, listingsCount, listingMode, settings, quickAdd, handle401]);

  const handleReset = useCallback(() => {
    setImages([]); setListing(null); setListingId(null); setError(null);
    setActiveListingIndex(0); setView(VIEW.UPLOAD);
    // Quick Add details are per-listing — reset them (re-seeds any saved location)
    setQuickAdd(buildDefaultQuickAdd());
  }, []);

  const handleSubscribe = useCallback(async (plan = 'monthly') => {
    if (!isAuthenticated) return;
    try {
      const res  = await fetch('/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan }),
      });
      if (res.status === 401) { handle401(); return; }
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setError('Could not start checkout. Please try again.');
    } catch (err) {
      console.error('[App] Stripe error:', err);
      setError('Could not start checkout. Please try again.');
    }
  }, [isAuthenticated, token, handle401]);

  // ── Standalone public pages (no auth required)
  if (window.location.pathname === '/privacy') return <PrivacyPolicy />;
  if (window.location.pathname === '/verify') return <VerifyEmailPage />;

  // ── Landing page for unauthenticated users
  if (!isAuthenticated) {
    return (
      <>
        <LandingPage
          onGetStarted={() => { setLoginSheetMode('register'); setShowLoginSheet(true); }}
          onSignIn={() => { setLoginSheetMode('login'); setShowLoginSheet(true); }}
        />
        {showLoginSheet && (
          <LoginSheet
            onLogin={handleLogin}
            onRegister={handleRegister}
            onGoogleLogin={handleGoogleLogin}
            onClose={() => setShowLoginSheet(false)}
            initialMode={loginSheetMode}
          />
        )}
      </>
    );
  }

  return (
    <div className="app-shell">
      <div className="safe-top" />

      {/* ── Header */}
      <header className="app-header">
        <div className="header-inner">
          <div className="logo">
            <img className="logo-mark-wrap" src="/media/logo-mascot.png" alt="" aria-hidden="true" />
            <span className="logo-text">sold4you</span>
          </div>
          <div className="header-right">
            {isAuthenticated && (
              isSubscribed ? (
                <span className="badge badge-pro">PRO</span>
              ) : promoActive ? (
                <span className="badge badge-pro">TESTER</span>
              ) : (
                <button className="badge badge-free" onClick={() => setShowPaywall(true)}>
                  {remainingFree}/{FREE_GENERATION_LIMIT} free
                </button>
              )
            )}
          </div>
        </div>
      </header>

      {/* ── Main */}
      <main className="app-main">
        {error && (
          <div className="error-banner" role="alert">
            <span>⚠ {error}</span>
            <button className="error-dismiss" onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* ── GENERATE TAB */}
        {activeTab === TAB.GENERATE && (
          <>
            {view === VIEW.UPLOAD && (
              <div className="view-upload">
                <div className="view-header">
                  <h1 className="view-title">New Listing</h1>
                  <p className="view-subtitle">Upload photos and get a ready-to-post listing in seconds.</p>
                </div>

                {/* Listing Mode Selector */}
                <div className="mode-group">
                  {LISTING_MODES.map((m) => (
                    <button
                      key={m.id}
                      className={`mode-card ${listingMode === m.id ? 'mode-card--active' : ''}`}
                      onClick={() => setListingMode(m.id)}
                    >
                      <span className="mode-icon">{m.icon}</span>
                      <span className="mode-label">{m.label}</span>
                      <span className="mode-desc">{m.desc}</span>
                    </button>
                  ))}
                </div>

                {listingMode === 3 && (
                  <p className="mode-notice">📋 Each photo will get its own separate listing block.</p>
                )}

                <ImageUploader
                  images={images}
                  onImagesChange={setImages}
                  maxImages={10}
                  minImages={1}
                />

                <QuickAddDetails
                  value={quickAdd}
                  onChange={setQuickAdd}
                  hasSavedLocation={hasSavedLocation}
                />

                {images.length >= 1 && (
                  <div className="action-area">
                    <button
                      className="btn-primary btn-generate"
                      onClick={handleGenerate}
                      disabled={isAtLimit}
                    >
                      <span>✦</span>
                      Generate {listingMode === 3 ? `${images.length} Listings` : 'Listing'}
                    </button>
                    {!hasAccess && (
                      <p className="usage-hint">
                        {remainingFree > 0
                          ? `${remainingFree} free generation${remainingFree !== 1 ? 's' : ''} remaining`
                          : "You've used all free generations — subscribe to continue"}
                      </p>
                    )}
                  </div>
                )}


                {/* Settings summary chip */}
                <button className="settings-summary" onClick={() => setActiveTab(TAB.SETTINGS)}>
                  <span>⚙️</span>
                  <span>
                    {settings.tone === 'short_sharp' ? 'Short & Sharp' : settings.tone === 'laugh' ? 'Bit of a Laugh' : 'Regular'} tone
                    {` · ${settings.listingStyle === 'minimalist' ? 'Minimalist' : 'Playful'}`}
                    {settings.condition ? ` · ${settings.condition}` : ''}
                  </span>
                  <span className="settings-summary__caret">›</span>
                </button>
              </div>
            )}

            {view === VIEW.LOADING && (
              <div className="view-loading" aria-live="polite">
                {/* ── Phase 0: Compressing — shown before the AI call starts */}
                {isCompressing ? (
                  <div className="compress-indicator">
                    <div className="compress-spinner" aria-hidden="true" />
                    <p className="compress-label">Preparing photos…</p>
                    <p className="compress-sub">Optimising {images.length} photo{images.length !== 1 ? 's' : ''} for upload</p>
                  </div>
                ) : (
                <>
                <div className="mascot-wrap">
                  <video
                    className="mascot-video"
                    src="/media/loading-mascot-v2.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    aria-hidden="true"
                  />
                </div>
                <p className="loading-step">{LOADING_STEPS[loadingStep]}</p>
                <div className="loading-bar-track">
                  <div className="loading-bar-fill" style={{ width: `${((loadingStep + 1) / LOADING_STEPS.length) * 100}%` }} />
                </div>
                </>
                )}
              </div>
            )}

            {view === VIEW.RESULT && listing && (
              <div className="view-result">
                <ListingResult
                  listing={listing}
                  images={images}
                  listingMode={listingMode}
                  activeIndex={activeListingIndex}
                  onIndexChange={setActiveListingIndex}
                  listingId={listingId}
                  token={token}
                  onListingRefined={(refined) => setListing((prev) => ({ ...prev, ...refined }))}
                />
                <div className="result-actions">
                  <button className="btn-secondary" onClick={handleReset}>+ New Listing</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── SETTINGS TAB */}
        {activeTab === TAB.SETTINGS && (
          <div className="view-settings">
            <div className="view-header">
              <h1 className="view-title">Settings</h1>
              <p className="view-subtitle">Customise how your listings are written. Changes apply to all future generations.</p>
            </div>
            <SettingsView
              settings={settings}
              onSettingsChange={setSettings}
              listingMode={listingMode}
            />
          </div>
        )}

        {/* ── ACCOUNT TAB */}
        {activeTab === TAB.ACCOUNT && (
          <div className="view-account">
            <div className="view-header">
              <h1 className="view-title">Account</h1>
              <p className="view-subtitle">Manage your profile, password, and subscription.</p>
            </div>
            <AccountView
              user={user}
              token={token}
              onLogout={handleLogout}
              onUserUpdated={(updated) => {
                setUser(updated);
                sessionStorage.setItem('fliplist_user', JSON.stringify(updated));
              }}
            />
          </div>
        )}
      </main>

      {/* ── Bottom Tab Nav */}
      {isAuthenticated && (
        <nav className="tab-nav" aria-label="Main navigation">
          <button
            className={`tab-btn ${activeTab === TAB.GENERATE ? 'tab-btn--active' : ''}`}
            onClick={() => setActiveTab(TAB.GENERATE)}
            aria-current={activeTab === TAB.GENERATE ? 'page' : undefined}
          >
            <svg className="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
            </svg>
            <span>Generate</span>
          </button>
          <button
            className={`tab-btn ${activeTab === TAB.SETTINGS ? 'tab-btn--active' : ''}`}
            onClick={() => setActiveTab(TAB.SETTINGS)}
            aria-current={activeTab === TAB.SETTINGS ? 'page' : undefined}
          >
            <svg className="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
            <span>Settings</span>
          </button>
          <button
            className={`tab-btn ${activeTab === TAB.ACCOUNT ? 'tab-btn--active' : ''}`}
            onClick={() => setActiveTab(TAB.ACCOUNT)}
            aria-current={activeTab === TAB.ACCOUNT ? 'page' : undefined}
          >
            <svg className="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
            <span>Account</span>
          </button>
        </nav>
      )}


      {/* ── Paywall */}
      {showPaywall && (
        <PaywallModal
          usedCount={listingsCount}
          limit={FREE_GENERATION_LIMIT}
          onSubscribe={handleSubscribe}
          onClose={() => setShowPaywall(false)}
        />
      )}

      {/* ── Global Styles */}
      <style>{`
        .app-shell {
          display: flex; flex-direction: column; height: 100dvh;
          max-width: 480px; margin: 0 auto; background: var(--color-bg-page);
        }

        .safe-top { height: env(safe-area-inset-top, 0px); background: var(--color-bg-card); flex-shrink: 0; }

        /* ── Header */
        .app-header { flex-shrink: 0; background: var(--color-bg-card); border-bottom: 0.5px solid var(--color-border); }
        .header-inner { display: flex; align-items: center; justify-content: space-between; padding: 13px 20px; }
        .logo { display: flex; align-items: center; gap: 8px; }
        .logo-mark-wrap { width: 36px; height: 36px; border-radius: 8px; object-fit: cover; flex-shrink: 0; display: block; }
        .logo-text { font-size: 16px; font-weight: 500; color: var(--color-text-heading); letter-spacing: -0.2px; }
        .header-right { display: flex; align-items: center; gap: 10px; }

        .badge { font-size: 11px; font-weight: 500; padding: 3px 10px; border-radius: var(--radius-full); border: none; cursor: pointer; font-family: inherit; }
        .badge-pro { background: var(--color-primary-light); color: var(--color-primary); }
        .badge-free { background: var(--color-bg-page); color: var(--color-text-secondary); border: 0.5px solid var(--color-border-dashed); }

        .btn-icon-sm { background: none; border: none; color: var(--color-text-hint); cursor: pointer; padding: 6px; border-radius: var(--radius-sm); display: flex; align-items: center; transition: color 0.15s; -webkit-tap-highlight-color: transparent; }
        .btn-icon-sm:hover { color: var(--color-text-secondary); }

        /* ── Main */
        .app-main { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; padding: 20px 20px 0; background: var(--color-bg-page); }

        .error-banner { display: flex; align-items: center; justify-content: space-between; gap: 10px; background: rgba(220,38,38,0.07); border: 0.5px solid rgba(220,38,38,0.2); border-radius: var(--radius-md); padding: 12px 14px; margin-bottom: 16px; font-size: 13px; color: #DC2626; }
        .error-dismiss { background: none; border: none; color: #DC2626; cursor: pointer; font-size: 14px; flex-shrink: 0; }

        /* ── Views */
        .view-header { margin-bottom: 20px; }
        .view-title { font-size: 19px; font-weight: 500; color: var(--color-text-heading); margin-bottom: 4px; }
        .view-subtitle { font-size: 13px; color: var(--color-text-secondary); line-height: 1.6; }
        .view-upload { padding-bottom: 24px; }
        .view-settings { padding-bottom: 24px; }
        .view-account { padding-bottom: 24px; }
        .view-result { display: flex; flex-direction: column; gap: 24px; padding-bottom: 24px; }

        /* ── Listing mode selector */
        .mode-group { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 16px; }
        .mode-card {
          display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 12px 6px;
          background: var(--color-bg-card); border: 0.5px solid var(--color-border);
          border-radius: var(--radius-md); cursor: pointer;
          transition: border-color 0.15s, background 0.15s;
          -webkit-tap-highlight-color: transparent; font-family: inherit; text-align: center;
        }
        .mode-card--active { border-color: var(--color-primary); background: var(--color-primary-light); }
        .mode-icon { font-size: 20px; }
        .mode-label { font-size: 12px; font-weight: 500; color: var(--color-text-heading); }
        .mode-desc { font-size: 10px; color: var(--color-text-hint); line-height: 1.3; }
        .mode-notice {
          font-size: 12px; color: var(--color-tip-icon);
          background: var(--color-tip-bg); border: 0.5px solid var(--color-tip-border);
          border-radius: var(--radius-sm); padding: 8px 12px; margin-bottom: 12px; line-height: 1.4;
        }

        /* ── Action area */
        .action-area { margin-top: 20px; display: flex; flex-direction: column; align-items: center; gap: 8px; }
        .btn-generate { width: 100%; padding: 0 20px; font-size: 14px; font-weight: 500; display: flex; align-items: center; justify-content: center; gap: 8px; height: 44px; }
        .usage-hint { font-size: 12px; color: var(--color-text-hint); text-align: center; }
        .upload-hint { text-align: center; font-size: 13px; color: var(--color-text-hint); margin-top: 14px; }

        /* ── Settings summary chip */
        .settings-summary {
          display: flex; align-items: center; gap: 8px; margin-top: 16px; width: 100%;
          padding: 10px 14px; background: var(--color-bg-card);
          border: 0.5px solid var(--color-border); border-radius: var(--radius-md);
          font-family: inherit; font-size: 12px; color: var(--color-text-hint);
          cursor: pointer; text-align: left; transition: border-color 0.15s;
          -webkit-tap-highlight-color: transparent;
        }
        .settings-summary:hover { border-color: var(--color-border-dashed); }
        .settings-summary__caret { margin-left: auto; font-size: 16px; }

        /* ── Buttons */
        .btn-primary {
          background: var(--color-primary); color: #EAF3DE; border: none;
          border-radius: var(--radius-md); font-family: inherit; font-weight: 500;
          cursor: pointer; transition: background 0.15s, transform 0.1s;
          -webkit-tap-highlight-color: transparent;
        }
        .btn-primary:hover { background: var(--color-primary-hover); }
        .btn-primary:active { transform: scale(0.985); }
        .btn-primary:disabled { opacity: 0.35; pointer-events: none; }

        .btn-secondary {
          width: 100%; padding: 0 16px; height: 44px;
          background: var(--color-bg-card); color: var(--color-text-heading);
          border: 0.5px solid var(--color-border-dashed); border-radius: var(--radius-md);
          font-family: inherit; font-size: 14px; font-weight: 500;
          cursor: pointer; transition: background 0.15s; -webkit-tap-highlight-color: transparent;
        }
        .btn-secondary:active { background: var(--color-bg-page); }

        .result-actions { display: flex; flex-direction: column; gap: 12px; }

        /* ── Compressing indicator */
        .compress-indicator {
          display: flex; flex-direction: column; align-items: center; gap: 10px;
        }
        .compress-spinner {
          width: 36px; height: 36px; border-radius: 50%;
          border: 3px solid var(--color-primary-light);
          border-top-color: var(--color-primary);
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .compress-label { font-size: 15px; font-weight: 500; color: var(--color-text-heading); }
        .compress-sub { font-size: 12px; color: var(--color-text-hint); margin-top: -4px; }

        /* ── Loading — mascot */
        .view-loading {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 60px 20px; gap: 28px; min-height: 60dvh;
          background: var(--color-bg-page);
        }
        .mascot-wrap { width: 160px; height: 160px; display: flex; align-items: center; justify-content: center; }
        .mascot-video { width: 100%; height: 100%; object-fit: contain; border-radius: 12px; }

        .loading-step { font-size: 14px; font-weight: 500; color: var(--color-text-secondary); min-height: 24px; text-align: center; }
        .loading-bar-track { width: 200px; height: 3px; background: var(--color-border); border-radius: 9999px; overflow: hidden; }
        .loading-bar-fill { height: 100%; background: var(--color-primary); border-radius: 9999px; transition: width 0.6s ease; }

        /* ── Tab Nav */
        .tab-nav {
          flex-shrink: 0; display: flex;
          background: var(--color-bg-card);
          border-top: 0.5px solid var(--color-border);
          padding-bottom: env(safe-area-inset-bottom, 0px);
        }
        .tab-btn {
          flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px;
          padding: 10px 0 12px; background: none; border: none;
          color: var(--color-text-hint); font-family: inherit; font-size: 10px; font-weight: 500;
          cursor: pointer; transition: color 0.15s; -webkit-tap-highlight-color: transparent;
        }
        .tab-btn--active { color: var(--color-primary); }
        .tab-icon { width: 22px; height: 22px; stroke-width: 1.75; }
        .tab-btn--active .tab-icon { stroke: var(--color-primary); }
      `}</style>
    </div>
  );
}
